import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import { VENDOR_ANIMAL_CATALOG } from '../data/vendorCatalog';
import { VendorEvents } from '../components/vendor/VendorEvents';
import {
  LogOut, User, Inbox, CheckCircle, XCircle,
  MessageSquare, MapPin, Calendar, Users, ChevronDown,
  Save, Plus, Trash2, Edit3, AlertTriangle, Clock,
  TrendingUp, DollarSign, BarChart3, History, Undo2
} from 'lucide-react';

const FONT_D = { fontFamily: 'Playfair Display, serif' };
const FONT_B = { fontFamily: 'Manrope, sans-serif' };

const HISTORY_BADGE_CLS = {
  approved: 'text-emerald-400 bg-emerald-400/10',
  denied: 'text-red-400 bg-red-400/10',
};
const HISTORY_BADGE_LABEL = {
  approved: 'Approved',
  denied: 'Declined',
};
const HISTORY_HEADINGS = {
  all: 'All Events',
  approved: 'Approved Events',
  denied: 'Declined Events',
};
const RESPOND_TOAST = {
  approve: 'Approved',
  deny: 'Declined',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
}

function formatCurrency(n) {
  if (!n && n !== 0) return '$0';
  return '$' + Number(n).toLocaleString();
}

/* ═══════════ BOTTOM NAV ═══════════ */
function BottomNav({ active, onChange, pendingCount }) {
  const tabs = [
    { id: 'requests', label: 'Requests', icon: Inbox, badge: pendingCount },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'history', label: 'History', icon: BarChart3 },
    { id: 'profile', label: 'Profile', icon: User },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-2xl border-t border-white/[0.05]" data-testid="vendor-tabs">
      <div className="max-w-lg mx-auto flex">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className="flex-1 flex flex-col items-center gap-0.5 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] relative"
            data-testid={`tab-${t.id}`}
          >
            <div className="relative">
              <t.icon className={`w-[22px] h-[22px] transition-all duration-300 ${active === t.id ? 'text-[#D4A843]' : 'text-neutral-600'}`} strokeWidth={active === t.id ? 2 : 1.5} />
              {t.badge > 0 && (
                <span className="absolute -top-1.5 -right-2.5 w-4 h-4 bg-[#EF4444] rounded-full flex items-center justify-center text-[9px] text-white font-bold">{t.badge}</span>
              )}
            </div>
            <span className={`text-[10px] font-semibold tracking-wide transition-all duration-300 ${active === t.id ? 'text-[#D4A843]' : 'text-neutral-600'}`} style={FONT_B}>{t.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}

/* ═══════════ REQUEST CARD ═══════════ */
function RequestCard({ request, onRespond, index }) {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const isPending = request.status === 'pending';
  const ev = request.event_info || request.proposal_info || {};

  const itemTotal = request.items?.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0) || 0;

  const handleAction = async (action) => {
    setLoading(true);
    await onRespond(request.request_id, action, notes);
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
      data-testid={`request-card-${request.request_id}`}
    >
      {/* Glow effect for pending */}
      {isPending && (
        <div className="absolute -inset-[1px] rounded-[28px] bg-gradient-to-b from-[#D4A843]/20 via-[#D4A843]/5 to-transparent pointer-events-none" />
      )}

      <div className={`relative bg-[#141414] rounded-[26px] overflow-hidden border ${isPending ? 'border-[#D4A843]/15' : 'border-white/[0.05]'}`}>
        {/* Top accent line for pending */}
        {isPending && <div className="h-[2px] bg-gradient-to-r from-transparent via-[#D4A843]/60 to-transparent" />}

        <div className="w-full text-left p-6">
          {/* Row 1: Event type + total value */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-white text-xl font-medium tracking-tight leading-tight" style={FONT_D}>
                {ev.event_type || 'Event Request'}
              </h3>
              <p className="text-neutral-500 text-sm mt-1" style={FONT_B}>
                {formatDate(ev.event_date)}
              </p>
            </div>
            <div className="text-right ml-4">
              <p className="text-[#D4A843] text-xl font-semibold tracking-tight" style={FONT_B}>
                {formatCurrency(itemTotal)}
              </p>
              <p className="text-neutral-600 text-xs mt-0.5" style={FONT_B}>
                {request.items?.length} item{request.items?.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Row 2: Meta chips */}
          <div className="flex flex-wrap gap-2 mb-5">
            {ev.event_location && (
              <span className="inline-flex items-center gap-1.5 text-neutral-400 text-xs bg-white/[0.04] px-3 py-1.5 rounded-full" style={FONT_B}>
                <MapPin className="w-3 h-3 text-neutral-600" strokeWidth={2} />
                {ev.event_location}
              </span>
            )}
            {ev.guest_count > 0 && (
              <span className="inline-flex items-center gap-1.5 text-neutral-400 text-xs bg-white/[0.04] px-3 py-1.5 rounded-full" style={FONT_B}>
                <Users className="w-3 h-3 text-neutral-600" strokeWidth={2} />
                {ev.guest_count} guests
              </span>
            )}
          </div>

          {/* Row 3: Item list */}
          <div className="space-y-[2px] rounded-2xl overflow-hidden">
            {request.items?.map((item, i) => (
              <div key={item.id + i} className="flex items-center justify-between bg-white/[0.025] px-4 py-3.5 first:rounded-t-2xl last:rounded-b-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#D4A843]/[0.08] flex items-center justify-center flex-shrink-0">
                    <span className="text-[#D4A843] text-xs font-bold" style={FONT_B}>{item.quantity || 1}</span>
                  </div>
                  <span className="text-white/90 text-sm font-medium" style={FONT_B}>{item.name}</span>
                </div>
                <span className="text-neutral-400 text-sm" style={FONT_B}>{formatCurrency(item.price)}</span>
              </div>
            ))}
          </div>

          {/* Status badge for non-pending */}
          {!isPending && (
            <div className="flex justify-center mt-4">
              <span className={`text-xs font-bold tracking-wide uppercase px-4 py-1.5 rounded-full ${HISTORY_BADGE_CLS[request.status] || 'text-blue-400 bg-blue-400/10'}`} style={FONT_B}>
                {HISTORY_BADGE_LABEL[request.status] || 'Changes Sent'}
              </span>
            </div>
          )}
        </div>

        {/* Action area - always visible for pending */}
        {isPending && (
          <div className="px-6 pb-6" data-testid={`request-actions-${request.request_id}`}>
            <div className="h-[1px] bg-gradient-to-r from-transparent via-white/[0.06] to-transparent mb-5" />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a note..."
              className="w-full bg-white/[0.03] text-white text-sm px-4 py-3.5 rounded-2xl border border-white/[0.05] focus:border-[#D4A843]/30 focus:outline-none resize-none mb-4 placeholder:text-neutral-700"
              style={FONT_B}
              rows={2}
              data-testid={`notes-${request.request_id}`}
            />
            <button
              onClick={() => handleAction('approve')}
              disabled={loading}
              className="w-full h-[52px] bg-[#D4A843] text-black text-sm font-bold rounded-full shadow-[0_0_30px_rgba(212,168,67,0.2)] active:scale-[0.97] transition-all disabled:opacity-30 mb-3 tracking-wide"
              style={FONT_B}
              data-testid={`approve-${request.request_id}`}
            >
              Approve Request
            </button>
            <button
              onClick={() => handleAction('deny')}
              disabled={loading}
              className="w-full h-12 bg-white/[0.04] border border-white/[0.08] text-neutral-400 text-sm font-semibold rounded-full active:scale-[0.97] transition-all disabled:opacity-30 flex items-center justify-center gap-2"
              style={FONT_B}
              data-testid={`deny-${request.request_id}`}
            >
              <XCircle className="w-4 h-4" strokeWidth={1.5} /> Decline
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ═══════════ HISTORY TAB ═══════════ */
function HistoryTab({ stats }) {
  const [history, setHistory] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/api/vendor/history')
      .then(d => { setHistory(d.history); setLoaded(true); })
      .catch((err) => { console.error('Failed to load vendor history', err); setLoaded(true); });
  }, []);

  const totalRevenue = history.filter(h => h.status === 'approved').reduce((s, h) => s + (h.revenue || 0), 0);
  const approvedCount = history.filter(h => h.status === 'approved').length;
  const deniedCount = history.filter(h => h.status === 'denied').length;
  const escalatedCount = history.filter(h => h.status === 'expired').length;

  const filtered = history.filter(h => {
    if (historyFilter !== 'all' && h.status !== historyFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const eventType = (h.event?.event_type || '').toLowerCase();
      const location = (h.event?.event_location || '').toLowerCase();
      const items = (h.items || []).map(i => i.name.toLowerCase()).join(' ');
      if (!eventType.includes(q) && !location.includes(q) && !items.includes(q)) return false;
    }
    return true;
  });

  const STATUS_BADGE = {
    approved: { label: 'Approved', icon: CheckCircle, bg: 'bg-emerald-500/10 border-emerald-500/20', text: 'text-emerald-400' },
    denied: { label: 'Declined', icon: XCircle, bg: 'bg-red-500/10 border-red-500/20', text: 'text-red-400' },
    expired: { label: 'Escalated', icon: Clock, bg: 'bg-amber-500/10 border-amber-500/20', text: 'text-amber-400' },
    changes_requested: { label: 'Modified', icon: MessageSquare, bg: 'bg-blue-500/10 border-blue-500/20', text: 'text-blue-400' },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-lg mx-auto px-6 pb-28"
    >
      <div className="pt-14 pb-6">
        <p className="text-neutral-500 text-sm font-medium tracking-wide mb-1" style={FONT_B}>Performance</p>
        <h1 className="text-white text-3xl font-medium tracking-tight" style={FONT_D}>History</h1>
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-2 gap-3 mb-8">
        <div className="bg-[#141414] border border-white/[0.05] rounded-3xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-[#D4A843]/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
          <DollarSign className="w-5 h-5 text-[#D4A843]/50 mb-3" strokeWidth={1.5} />
          <p className="text-[#D4A843] text-2xl font-semibold tracking-tight" style={FONT_B}>{formatCurrency(totalRevenue)}</p>
          <p className="text-neutral-600 text-xs mt-1" style={FONT_B}>Total Revenue</p>
        </div>
        <div className="bg-[#141414] border border-white/[0.05] rounded-3xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
          <TrendingUp className="w-5 h-5 text-emerald-500/50 mb-3" strokeWidth={1.5} />
          <p className="text-emerald-400 text-2xl font-semibold tracking-tight" style={FONT_B}>{approvedCount}</p>
          <p className="text-neutral-600 text-xs mt-1" style={FONT_B}>Events Fulfilled</p>
        </div>
      </div>

      {/* Approval rate */}
      {history.length > 0 && (
        <div className="bg-[#141414] border border-white/[0.05] rounded-3xl p-5 mb-8">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white text-sm font-medium" style={FONT_B}>Approval Rate</p>
            <p className="text-[#D4A843] text-sm font-bold" style={FONT_B}>
              {history.length > 0 ? Math.round((approvedCount / history.length) * 100) : 0}%
            </p>
          </div>
          <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: history.length > 0 ? `${(approvedCount / history.length) * 100}%` : '0%' }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="h-full bg-gradient-to-r from-[#D4A843] to-[#E5B954] rounded-full"
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-neutral-600" style={FONT_B}>
            <span>{approvedCount} approved</span>
            <span>{deniedCount} declined</span>
            {escalatedCount > 0 && <span>{escalatedCount} escalated</span>}
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="mb-5">
        <div className="relative mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search events, locations, animals..."
            className="w-full bg-white/[0.03] text-white text-sm pl-10 pr-4 py-3 rounded-2xl border border-white/[0.06] focus:border-[#D4A843]/30 focus:outline-none placeholder:text-neutral-700"
            style={FONT_B}
            data-testid="history-search"
          />
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
        </div>
        <div className="flex gap-2 overflow-x-auto" data-testid="history-filters">
          {[
            { id: 'all', label: 'All', count: history.length },
            { id: 'approved', label: 'Approved', count: approvedCount },
            { id: 'denied', label: 'Declined', count: deniedCount },
            ...(escalatedCount > 0 ? [{ id: 'expired', label: 'Escalated', count: escalatedCount }] : []),
          ].map(f => (
            <button key={f.id} onClick={() => setHistoryFilter(f.id)}
              className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 ${
                historyFilter === f.id ? 'bg-white text-black shadow-[0_2px_12px_rgba(255,255,255,0.1)]' : 'text-neutral-500 bg-white/[0.03] border border-white/[0.06]'
              }`} style={FONT_B} data-testid={`history-filter-${f.id}`}>
              {f.label} {f.count}
            </button>
          ))}
        </div>
      </div>

      {/* Event Timeline */}
      <h3 className="text-white text-lg font-medium mb-4" style={FONT_D}>
        {HISTORY_HEADINGS[historyFilter] || 'Escalated Events'}
      </h3>
      <div className="space-y-3" data-testid="history-list">
        {!loaded ? (
          <div className="text-neutral-600 text-sm text-center py-12" style={FONT_B}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16">
            <div className="w-14 h-14 rounded-full bg-white/[0.03] flex items-center justify-center mb-4">
              <History className="w-6 h-6 text-neutral-700" strokeWidth={1.5} />
            </div>
            <p className="text-neutral-600 text-sm" style={FONT_B}>
              {search ? 'No results found' : 'No history yet'}
            </p>
            <p className="text-neutral-700 text-xs mt-1" style={FONT_B}>
              {search ? 'Try a different search' : 'Completed events will appear here'}
            </p>
          </div>
        ) : filtered.map((h, i) => {
          const badge = STATUS_BADGE[h.status] || STATUS_BADGE.approved;
          const BadgeIcon = badge.icon;
          return (
            <motion.div
              key={h.request_id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-[#141414] border border-white/[0.05] rounded-2xl p-5"
              data-testid={`history-${h.request_id}`}
            >
              {/* Status badge - prominent at top */}
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border mb-3 ${badge.bg}`}>
                <BadgeIcon className={`w-3.5 h-3.5 ${badge.text}`} strokeWidth={2} />
                <span className={`text-xs font-bold tracking-wide uppercase ${badge.text}`} style={FONT_B}>
                  {badge.label}
                </span>
              </div>

              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-white text-base font-medium" style={FONT_D}>{h.event?.event_type || 'Event'}</h4>
                  <p className="text-neutral-500 text-xs mt-0.5" style={FONT_B}>{formatDate(h.event?.event_date)}</p>
                </div>
                <div className="text-right">
                  {h.status === 'approved' && <p className="text-[#D4A843] text-base font-semibold" style={FONT_B}>{formatCurrency(h.revenue)}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {h.items?.map((item, j) => (
                  <span key={j} className="text-xs text-neutral-500 bg-white/[0.03] px-2.5 py-1 rounded-lg" style={FONT_B}>{item.name}</span>
                ))}
              </div>
              {h.event?.event_location && (
                <p className="text-neutral-600 text-xs mt-3 flex items-center gap-1.5" style={FONT_B}>
                  <MapPin className="w-3 h-3" strokeWidth={1.5} /> {h.event.event_location}
                </p>
              )}
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ═══════════ PROFILE TAB ═══════════ */
function ProfileTab() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [formDirty, setFormDirty] = useState(false);
  const [animalList, setAnimalList] = useState([]);
  const [savedAnimalList, setSavedAnimalList] = useState([]);
  const [animalsDirty, setAnimalsDirty] = useState(false);
  const [packages, setPackages] = useState([]);
  const [savedPackages, setSavedPackages] = useState([]);
  const [packagesDirty, setPackagesDirty] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customAnimal, setCustomAnimal] = useState({ name: '', baseRate: '', additionalHourRate: '' });
  const [showPackageForm, setShowPackageForm] = useState(false);
  const [newPackage, setNewPackage] = useState({ name: '', description: '', animals: [], price: '', duration: '' });
  const [catalogSearch, setCatalogSearch] = useState('');

  const loadProfile = useCallback(async () => {
    try {
      const data = await api.get('/api/vendor/profile');
      setProfile(data);
      const f = {
        name: data.name || '', phone: data.phone || '', email: data.email || '',
        address: data.address || '', description: data.description || '',
        bio: data.bio || '', license_number: data.license_number || '',
        insurance_info: data.insurance_info || '',
      };
      setForm(f);
      setFormDirty(false);
      const animals = data.products || [];
      setAnimalList(animals);
      setSavedAnimalList(JSON.parse(JSON.stringify(animals)));
      setAnimalsDirty(false);
      const pkgs = data.packages || [];
      setPackages(pkgs);
      setSavedPackages(JSON.parse(JSON.stringify(pkgs)));
      setPackagesDirty(false);
    } catch (err) {
      console.error('Failed to load vendor profile', err);
      toast.error('Failed to load profile');
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const updateForm = (field, value) => {
    setForm({ ...form, [field]: value });
    setFormDirty(true);
  };

  const saveInfo = async () => {
    setSaving(true);
    try {
      const updated = await api.put('/api/vendor/profile', form);
      setProfile(updated);
      setFormDirty(false);
      toast.success('Profile saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const saveAnimals = async () => {
    setSaving(true);
    try {
      const updated = await api.put('/api/vendor/profile', { products: animalList });
      setProfile(updated);
      setSavedAnimalList(JSON.parse(JSON.stringify(animalList)));
      setAnimalsDirty(false);
      toast.success('Animals & pricing saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const savePackages = async () => {
    setSaving(true);
    try {
      const updated = await api.put('/api/vendor/profile', { packages });
      setProfile(updated);
      setSavedPackages(JSON.parse(JSON.stringify(packages)));
      setPackagesDirty(false);
      toast.success('Packages saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const undoAnimals = () => {
    setAnimalList(JSON.parse(JSON.stringify(savedAnimalList)));
    setAnimalsDirty(false);
    toast('Changes reverted');
  };

  const addAnimalFromCatalog = (item) => {
    if (animalList.find(a => a.id === item.id)) return;
    setAnimalList([...animalList, {
      id: item.id, name: item.name, category: item.category,
      baseRate: item.sitePrice || 0, additionalHourRate: item.siteAdditionalHour || 0,
      isCustom: false,
    }]);
    setAnimalsDirty(true);
    setCatalogSearch('');
  };

  const addCustomAnimal = () => {
    if (!customAnimal.name) return;
    const id = `custom-${Date.now()}`;
    setAnimalList([...animalList, {
      id, name: customAnimal.name, category: 'Custom',
      baseRate: parseFloat(customAnimal.baseRate) || 0,
      additionalHourRate: parseFloat(customAnimal.additionalHourRate) || 0,
      isCustom: true,
    }]);
    setAnimalsDirty(true);
    setCustomAnimal({ name: '', baseRate: '', additionalHourRate: '' });
    setShowCustomForm(false);
  };

  const removeAnimal = (id) => { setAnimalList(animalList.filter(a => a.id !== id)); setAnimalsDirty(true); };
  const updateAnimalField = (id, field, value) => {
    setAnimalList(animalList.map(a => a.id === id ? { ...a, [field]: parseFloat(value) || 0 } : a));
    setAnimalsDirty(true);
  };

  const addPackage = () => {
    if (!newPackage.name || newPackage.animals.length === 0) {
      toast.error('Package needs a name and at least one animal');
      return;
    }
    const id = `pkg-${Date.now()}`;
    setPackages([...packages, { ...newPackage, id, price: parseFloat(newPackage.price) || 0, duration: parseInt(newPackage.duration) || 1 }]);
    setPackagesDirty(true);
    setNewPackage({ name: '', description: '', animals: [], price: '', duration: '' });
    setShowPackageForm(false);
  };

  const removePackage = (id) => { setPackages(packages.filter(p => p.id !== id)); setPackagesDirty(true); };
  const togglePackageAnimal = (animalId) => {
    setNewPackage(prev => ({
      ...prev,
      animals: prev.animals.includes(animalId)
        ? prev.animals.filter(a => a !== animalId)
        : [...prev.animals, animalId]
    }));
  };

  const availableCatalog = VENDOR_ANIMAL_CATALOG.filter(c => !animalList.find(a => a.id === c.id));
  const filteredCatalog = catalogSearch
    ? availableCatalog.filter(c => c.name.toLowerCase().includes(catalogSearch.toLowerCase()) || c.category.toLowerCase().includes(catalogSearch.toLowerCase()))
    : availableCatalog;
  const groupedCatalog = filteredCatalog.reduce((acc, i) => { acc[i.category] = acc[i.category] || []; acc[i.category].push(i); return acc; }, {});

  const handleLogout = () => { logout(); navigate('/vendor/login'); };

  if (!profile) return <div className="flex items-center justify-center h-64"><div className="text-neutral-600 text-sm" style={FONT_B}>Loading...</div></div>;

  const InputField = ({ label, field, type = 'text', multiline }) => (
    <div className="py-4 border-b border-white/[0.04] last:border-0">
      <label className="text-neutral-500 text-[10px] font-bold tracking-[0.15em] uppercase block mb-2" style={FONT_B}>{label}</label>
      {multiline ? (
        <textarea value={form[field] || ''} onChange={(e) => updateForm(field, e.target.value)}
          placeholder={`Enter ${label.toLowerCase()}...`}
          className="w-full bg-white/[0.03] text-white text-[15px] border border-white/[0.06] rounded-xl px-4 py-3 focus:border-[#D4A843]/40 focus:outline-none resize-none placeholder:text-neutral-700" style={FONT_B} rows={2} data-testid={`profile-${field}`} />
      ) : (
        <input type={type} value={form[field] || ''} onChange={(e) => updateForm(field, e.target.value)}
          placeholder={`Enter ${label.toLowerCase()}...`}
          className="w-full bg-white/[0.03] text-white text-[15px] border border-white/[0.06] rounded-xl px-4 py-3 focus:border-[#D4A843]/40 focus:outline-none placeholder:text-neutral-700" style={FONT_B} data-testid={`profile-${field}`} />
      )}
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-6 pb-28 max-w-lg mx-auto" data-testid="vendor-profile">
      {/* Avatar header */}
      <div className="flex items-center gap-4 pt-14 pb-3">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4A843]/20 to-[#D4A843]/5 border border-[#D4A843]/10 flex items-center justify-center flex-shrink-0">
          <span className="text-[#D4A843] text-2xl font-medium" style={FONT_D}>{profile.name?.charAt(0)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-white text-xl font-medium truncate" style={FONT_D}>{profile.name}</h2>
          <p className="text-neutral-600 text-sm" style={FONT_B}>{profile.email}</p>
        </div>
        <button onClick={handleLogout} className="w-10 h-10 rounded-full bg-white/[0.03] flex items-center justify-center text-neutral-600 hover:text-red-400 transition-colors" data-testid="vendor-logout-btn">
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
      <p className="text-[#D4A843]/40 text-[10px] font-bold tracking-[0.2em] uppercase mb-8 ml-[80px]" style={FONT_B}>Exotic Animals Miami</p>

      {/* Business Info */}
      <div className="bg-[#141414] border border-white/[0.05] rounded-3xl p-6 mb-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white text-lg font-medium" style={FONT_D}>Business Info</h3>
          {formDirty && (
            <button onClick={saveInfo} disabled={saving} className="bg-[#D4A843] text-black text-xs font-bold px-5 py-2 rounded-full active:scale-95 transition-all disabled:opacity-30" style={FONT_B} data-testid="save-info-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
        <InputField label="Business Name" field="name" />
        <InputField label="Phone" field="phone" type="tel" />
        <InputField label="Email" field="email" type="email" />
        <InputField label="Address" field="address" />
        <InputField label="About" field="description" multiline />
        <InputField label="License Number" field="license_number" />
        <InputField label="Insurance" field="insurance_info" />
      </div>

      {/* Animals & Pricing */}
      <div className="bg-[#141414] border border-white/[0.05] rounded-3xl overflow-hidden mb-6">
        <div className="flex items-center justify-between p-6 pb-4">
          <h3 className="text-white text-lg font-medium" style={FONT_D}>Animals & Pricing</h3>
          <div className="flex gap-2">
            {animalsDirty && (
              <button onClick={undoAnimals} className="bg-white/[0.05] border border-white/[0.08] text-neutral-400 text-xs font-medium px-3 py-1.5 rounded-full active:scale-95 transition-all flex items-center gap-1" style={FONT_B} data-testid="undo-animals-btn">
                <Undo2 className="w-3 h-3" strokeWidth={2} /> Undo
              </button>
            )}
            <button onClick={() => { setShowCatalog(!showCatalog); setShowCustomForm(false); }} className="bg-white/[0.05] border border-white/[0.08] text-white text-xs font-medium px-3.5 py-1.5 rounded-full active:scale-95 transition-all flex items-center gap-1" style={FONT_B} data-testid="add-animal-btn">
              <Plus className="w-3 h-3" strokeWidth={2.5} /> From Catalog
            </button>
            <button onClick={() => { setShowCustomForm(!showCustomForm); setShowCatalog(false); }} className="bg-white/[0.05] border border-white/[0.08] text-white text-xs font-medium px-3.5 py-1.5 rounded-full active:scale-95 transition-all flex items-center gap-1" style={FONT_B} data-testid="add-custom-btn">
              <Edit3 className="w-3 h-3" strokeWidth={2} /> Custom
            </button>
            {animalsDirty && (
              <button onClick={saveAnimals} disabled={saving} className="bg-[#D4A843] text-black text-xs font-bold px-3.5 py-1.5 rounded-full active:scale-95 transition-all disabled:opacity-30" style={FONT_B} data-testid="save-animals-btn">Save</button>
            )}
          </div>
        </div>

        {/* Catalog picker */}
        <AnimatePresence>
          {showCatalog && (
            <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
              <div className="px-6 pb-4">
                <input
                  type="text" value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)}
                  placeholder="Search animals..."
                  className="w-full bg-white/[0.03] text-white text-sm border border-white/[0.06] rounded-xl px-4 py-2.5 mb-3 focus:border-[#D4A843]/30 focus:outline-none placeholder:text-neutral-700"
                  style={FONT_B} data-testid="catalog-search"
                />
                <div className="max-h-60 overflow-y-auto" data-testid="catalog-list">
                  {Object.entries(groupedCatalog).map(([cat, items]) => (
                    <div key={cat} className="mb-4">
                      <p className="text-neutral-600 text-[10px] font-bold tracking-[0.15em] uppercase mb-2" style={FONT_B}>{cat}</p>
                      <div className="flex flex-wrap gap-2">
                        {items.map(item => (
                          <button key={item.id} onClick={() => addAnimalFromCatalog(item)} className="text-xs text-[#D4A843] bg-[#D4A843]/[0.06] border border-[#D4A843]/15 px-3 py-1.5 rounded-full active:scale-95 transition-transform" style={FONT_B} data-testid={`catalog-${item.id}`}>
                            + {item.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {Object.keys(groupedCatalog).length === 0 && (
                    <p className="text-neutral-700 text-sm text-center py-4" style={FONT_B}>
                      {catalogSearch ? 'No matching animals' : 'All animals added'}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Custom animal form */}
        <AnimatePresence>
          {showCustomForm && (
            <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
              <div className="px-6 pb-4 space-y-3">
                <p className="text-neutral-500 text-[10px] font-bold tracking-[0.15em] uppercase" style={FONT_B}>Add Custom Animal</p>
                <input type="text" value={customAnimal.name} onChange={(e) => setCustomAnimal({ ...customAnimal, name: e.target.value })}
                  placeholder="Animal name" className="w-full bg-white/[0.03] text-white text-sm border border-white/[0.06] rounded-xl px-4 py-2.5 focus:border-[#D4A843]/30 focus:outline-none placeholder:text-neutral-700" style={FONT_B} data-testid="custom-name" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-neutral-600 text-[10px] block mb-1" style={FONT_B}>Base Rate / Hour ($)</label>
                    <input type="number" value={customAnimal.baseRate} onChange={(e) => setCustomAnimal({ ...customAnimal, baseRate: e.target.value })}
                      placeholder="0" className="w-full bg-white/[0.03] text-[#D4A843] text-sm border border-white/[0.06] rounded-xl px-4 py-2.5 focus:border-[#D4A843]/30 focus:outline-none" style={FONT_B} data-testid="custom-base-rate" />
                  </div>
                  <div>
                    <label className="text-neutral-600 text-[10px] block mb-1" style={FONT_B}>Additional Hour ($)</label>
                    <input type="number" value={customAnimal.additionalHourRate} onChange={(e) => setCustomAnimal({ ...customAnimal, additionalHourRate: e.target.value })}
                      placeholder="0" className="w-full bg-white/[0.03] text-[#D4A843] text-sm border border-white/[0.06] rounded-xl px-4 py-2.5 focus:border-[#D4A843]/30 focus:outline-none" style={FONT_B} data-testid="custom-additional-rate" />
                  </div>
                </div>
                <button onClick={addCustomAnimal} className="bg-[#D4A843] text-black text-xs font-bold px-5 py-2 rounded-full active:scale-95 transition-all w-full" style={FONT_B} data-testid="add-custom-submit">Add Animal</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Animal list */}
        <div className="px-3">
          {animalList.length === 0 ? (
            <p className="text-neutral-700 text-sm text-center py-10" style={FONT_B}>No animals yet — add from the catalog or create a custom one</p>
          ) : animalList.map(a => (
            <div key={a.id} className="px-3 py-3 rounded-2xl hover:bg-white/[0.015] transition-colors" data-testid={`animal-${a.id}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <span className="text-white/80 text-sm font-medium truncate block" style={FONT_B}>{a.name}</span>
                  <span className="text-neutral-600 text-[10px]" style={FONT_B}>{a.category}{a.isCustom ? ' (Custom)' : ''}</span>
                </div>
                <button onClick={() => removeAnimal(a.id)} className="w-8 h-8 flex items-center justify-center text-neutral-700 hover:text-red-400 transition-colors rounded-full" data-testid={`remove-animal-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-neutral-600 text-[9px] uppercase tracking-wider block mb-1" style={FONT_B}>Base Rate/Hr</label>
                  <div className="flex items-center">
                    <span className="text-neutral-600 text-sm mr-1">$</span>
                    <input type="number" value={a.baseRate || ''} onChange={(e) => updateAnimalField(a.id, 'baseRate', e.target.value)}
                      placeholder="0" className="w-full bg-white/[0.04] border border-white/[0.06] text-[#D4A843] text-sm text-right px-3 py-2 rounded-xl focus:border-[#D4A843]/30 focus:outline-none" style={FONT_B} data-testid={`base-rate-${a.id}`} />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-neutral-600 text-[9px] uppercase tracking-wider block mb-1" style={FONT_B}>Add'l Hour</label>
                  <div className="flex items-center">
                    <span className="text-neutral-600 text-sm mr-1">$</span>
                    <input type="number" value={a.additionalHourRate || ''} onChange={(e) => updateAnimalField(a.id, 'additionalHourRate', e.target.value)}
                      placeholder="0" className="w-full bg-white/[0.04] border border-white/[0.06] text-[#D4A843] text-sm text-right px-3 py-2 rounded-xl focus:border-[#D4A843]/30 focus:outline-none" style={FONT_B} data-testid={`addl-rate-${a.id}`} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="h-3" />
      </div>

      {/* Packages / Bundles */}
      <div className="bg-[#141414] border border-white/[0.05] rounded-3xl overflow-hidden mb-6">
        <div className="flex items-center justify-between p-6 pb-4">
          <h3 className="text-white text-lg font-medium" style={FONT_D}>Packages</h3>
          <div className="flex gap-2">
            <button onClick={() => setShowPackageForm(!showPackageForm)} className="bg-white/[0.05] border border-white/[0.08] text-white text-xs font-medium px-3.5 py-1.5 rounded-full active:scale-95 transition-all flex items-center gap-1" style={FONT_B} data-testid="add-package-btn">
              <Plus className="w-3 h-3" strokeWidth={2.5} /> New Package
            </button>
            {packagesDirty && (
              <button onClick={savePackages} disabled={saving} className="bg-[#D4A843] text-black text-xs font-bold px-3.5 py-1.5 rounded-full active:scale-95 transition-all disabled:opacity-30" style={FONT_B} data-testid="save-packages-btn">Save</button>
            )}
          </div>
        </div>

        {/* New package form */}
        <AnimatePresence>
          {showPackageForm && (
            <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
              <div className="px-6 pb-4 space-y-3">
                <p className="text-neutral-500 text-[10px] font-bold tracking-[0.15em] uppercase" style={FONT_B}>Create Package Bundle</p>
                <input type="text" value={newPackage.name} onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                  placeholder="Package name (e.g. Safari Experience)" className="w-full bg-white/[0.03] text-white text-sm border border-white/[0.06] rounded-xl px-4 py-2.5 focus:border-[#D4A843]/30 focus:outline-none placeholder:text-neutral-700" style={FONT_B} data-testid="pkg-name" />
                <input type="text" value={newPackage.description} onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
                  placeholder="Description" className="w-full bg-white/[0.03] text-white text-sm border border-white/[0.06] rounded-xl px-4 py-2.5 focus:border-[#D4A843]/30 focus:outline-none placeholder:text-neutral-700" style={FONT_B} data-testid="pkg-description" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-neutral-600 text-[10px] block mb-1" style={FONT_B}>Package Price ($)</label>
                    <input type="number" value={newPackage.price} onChange={(e) => setNewPackage({ ...newPackage, price: e.target.value })}
                      placeholder="0" className="w-full bg-white/[0.03] text-[#D4A843] text-sm border border-white/[0.06] rounded-xl px-4 py-2.5 focus:border-[#D4A843]/30 focus:outline-none" style={FONT_B} data-testid="pkg-price" />
                  </div>
                  <div>
                    <label className="text-neutral-600 text-[10px] block mb-1" style={FONT_B}>Duration (hours)</label>
                    <input type="number" value={newPackage.duration} onChange={(e) => setNewPackage({ ...newPackage, duration: e.target.value })}
                      placeholder="1" className="w-full bg-white/[0.03] text-white text-sm border border-white/[0.06] rounded-xl px-4 py-2.5 focus:border-[#D4A843]/30 focus:outline-none" style={FONT_B} data-testid="pkg-duration" />
                  </div>
                </div>
                {/* Animal selector for package */}
                <div>
                  <label className="text-neutral-600 text-[10px] block mb-2" style={FONT_B}>Include Animals (tap to select)</label>
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto" data-testid="pkg-animal-selector">
                    {animalList.map(a => (
                      <button key={a.id} onClick={() => togglePackageAnimal(a.id)}
                        className={`text-xs px-3 py-1.5 rounded-full active:scale-95 transition-all ${
                          newPackage.animals.includes(a.id)
                            ? 'bg-[#D4A843] text-black font-bold'
                            : 'text-neutral-400 bg-white/[0.04] border border-white/[0.06]'
                        }`} style={FONT_B} data-testid={`pkg-toggle-${a.id}`}>
                        {a.name}
                      </button>
                    ))}
                    {animalList.length === 0 && <p className="text-neutral-700 text-xs" style={FONT_B}>Add animals first</p>}
                  </div>
                </div>
                <button onClick={addPackage} className="bg-[#D4A843] text-black text-xs font-bold px-5 py-2 rounded-full active:scale-95 transition-all w-full" style={FONT_B} data-testid="create-package-submit">Create Package</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Package list */}
        <div className="px-3">
          {packages.length === 0 ? (
            <p className="text-neutral-700 text-sm text-center py-10" style={FONT_B}>No packages yet — create bundled offerings for clients</p>
          ) : packages.map(pkg => (
            <div key={pkg.id} className="px-3 py-4 rounded-2xl hover:bg-white/[0.015] transition-colors" data-testid={`package-${pkg.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-white text-sm font-medium" style={FONT_B}>{pkg.name}</span>
                  <span className="text-[#D4A843] text-sm ml-3 font-semibold" style={FONT_B}>{formatCurrency(pkg.price)}</span>
                  {pkg.duration && <span className="text-neutral-600 text-xs ml-2" style={FONT_B}>{pkg.duration}hr</span>}
                </div>
                <button onClick={() => removePackage(pkg.id)} className="w-8 h-8 flex items-center justify-center text-neutral-700 hover:text-red-400 transition-colors rounded-full" data-testid={`remove-pkg-${pkg.id}`}>
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
              {pkg.description && <p className="text-neutral-600 text-xs mb-2" style={FONT_B}>{pkg.description}</p>}
              <div className="flex flex-wrap gap-1.5">
                {pkg.animals?.map(aid => {
                  const a = animalList.find(x => x.id === aid);
                  return <span key={aid} className="text-[10px] text-neutral-500 bg-white/[0.03] px-2 py-1 rounded-lg" style={FONT_B}>{a?.name || aid}</span>;
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="h-3" />
      </div>
    </motion.div>
  );
}

/* ═══════════ MAIN DASHBOARD ═══════════ */
export default function VendorDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('requests');
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');

  const loadData = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([api.get('/api/vendor/dashboard'), api.get('/api/vendor/requests')]);
      setStats(s); setRequests(r.requests);
    } catch (err) { console.error('Failed to load vendor dashboard', err); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRespond = async (requestId, action, notes) => {
    try {
      await api.post(`/api/vendor/requests/${requestId}/respond`, { action, notes: notes || null });
      toast.success(RESPOND_TOAST[action] || 'Changes sent');
      await loadData();
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const pending = stats?.pending_requests || 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="vendor-dashboard">
      <Toaster position="top-center" toastOptions={{
        style: { background: '#1A1A1A', color: '#fff', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', fontFamily: 'Manrope, sans-serif' },
      }} />

      <AnimatePresence mode="wait">
        {activeTab === 'requests' && (
          <motion.div key="requests" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="max-w-lg mx-auto px-6 pb-28">
            <div className="pt-14 pb-2">
              <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="text-neutral-500 text-sm font-medium mb-1" style={FONT_B}>Welcome back</motion.p>
              <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-white text-3xl font-medium tracking-tight" style={FONT_D}>Requests</motion.h1>
            </div>

            {pending > 0 && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#D4A843]/[0.06] border border-[#D4A843]/15 rounded-2xl px-5 py-4 my-6 flex items-center gap-3" data-testid="pending-alert">
                <div className="w-10 h-10 rounded-full bg-[#D4A843]/10 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-[#D4A843]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[#D4A843] text-sm font-semibold" style={FONT_B}>{pending} pending request{pending !== 1 ? 's' : ''}</p>
                  <p className="text-[#D4A843]/50 text-xs" style={FONT_B}>Tap to review and respond</p>
                </div>
              </motion.div>
            )}

            <div className="flex gap-2 mb-6 overflow-x-auto py-2 -mx-1 px-1" data-testid="request-filters">
              {[
                { id: 'pending', label: 'Pending', count: stats?.pending_requests },
                { id: 'denied', label: 'Declined', count: stats?.denied },
              ].map(f => (
                <button key={f.id} onClick={() => setFilter(f.id)}
                  className={`px-4 py-2.5 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 ${
                    filter === f.id ? 'bg-white text-black shadow-[0_2px_12px_rgba(255,255,255,0.1)]' : 'text-neutral-500 bg-white/[0.03] border border-white/[0.06]'
                  }`} style={FONT_B} data-testid={`filter-${f.id}`}>
                  {f.label}{f.count > 0 ? ` ${f.count}` : ''}
                </button>
              ))}
            </div>

            <div className="space-y-4" data-testid="vendor-requests-list">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center py-20">
                  <div className="w-16 h-16 rounded-full bg-white/[0.02] flex items-center justify-center mb-4">
                    <Inbox className="w-7 h-7 text-neutral-800" strokeWidth={1.5} />
                  </div>
                  <p className="text-neutral-600 text-sm" style={FONT_B}>No {filter !== 'all' ? filter : ''} requests</p>
                </div>
              ) : filtered.map((r, i) => (
                <RequestCard key={r.request_id} request={r} onRespond={handleRespond} index={i} />
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === 'events' && (
          <motion.div key="events" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <VendorEvents />
          </motion.div>
        )}

        {activeTab === 'history' && (
          <motion.div key="history" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <HistoryTab stats={stats} />
          </motion.div>
        )}

        {activeTab === 'profile' && (
          <motion.div key="profile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <ProfileTab />
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav active={activeTab} onChange={setActiveTab} pendingCount={pending} />
    </div>
  );
}
